import { Component, EventEmitter, Input, Output, ChangeDetectorRef, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { mergeXmlVsElementsIrsIntoFilterParams } from '../../shared/efile-ce-dialog-context';

@Component({
    selector: 'gtw-xml-vs-elements',
    templateUrl: './xml-vs-elements.component.html',
    styleUrls: ['./xml-vs-elements.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class XmlVsElementsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  /**
   * From the custom element (AngularJS `filter-params="{{...| json}}"`, CeHostDialog
   * `setAttribute('filter-params', JSON.stringify(...))`). Merged over
   * {@link normalizeFilterParams} so host context is not lost when WorkspaceService
   * has no active filters (typical in AngularJS-hosted modals).
   */
  private hostFilterParams: any = {};
  private hostRowData: any = {};

  filterParams: any = {};
  GENERAL_CONFIG: any = {};

  showModal = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Xml to Elements Comparison',
    message: 'Are you sure you want to run a new xml to elements comparison?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    this.hostFilterParams = this.parseBoundContext(filterParams);
    this.applyWorkspaceContext();
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    this.hostRowData = this.parseBoundContext(rowData);
    this.applyWorkspaceContext();
  }

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    const normalized = normalizeFilterParams(activeScreen);
    const rowFromScreen = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    const row = { ...rowFromScreen, ...this.hostRowData };
    const merged = { ...normalized, ...this.hostFilterParams };
    this.filterParams = mergeXmlVsElementsIrsIntoFilterParams(merged, row);
    this.cdr.markForCheck();
  }

  /**
   * Resolve `irs_id` at confirm time by scanning every source the legacy
   * `XmlVsElementsController` flow can populate. Legacy reads
   * `workspaceFactory.activeScreen.filters.getFilterParams().irs_id` directly;
   * the migrated CE may receive that same value through any of:
   *   - `this.filterParams` (already normalized via mergeXmlVsElementsIrsIntoFilterParams)
   *   - `hostFilterParams` (CE attribute `filter-params` set by legacy
   *     AngularJS host `vm.filterParams | json` or by CeHostDialog)
   *   - `hostRowData` (CE attribute `row-data` from the clicked grid row)
   *   - live WorkspaceService activeScreen filters / data
   *   - `GlobalService.globalParams` (when the screen filter was promoted
   *     to global params, e.g. tab restore or shared screens)
   *
   * The first non-empty value wins, mirroring legacy behavior. Returns ''
   * when nothing is available so the caller can warn the user.
   */
  private resolveIrsId(): string {
    const liveActive = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const liveFilters = normalizeFilterParams(liveActive);
    const liveRow = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(liveActive));
    const globalParams = (this.gtwGlobal as any)?.global?.globalParams || {};

    const sources: any[] = [
      this.filterParams,
      this.hostFilterParams,
      liveFilters,
      this.hostRowData,
      liveRow,
      globalParams,
    ];

    const irsKeys = [
      'irs_id',
      'IRS_ID',
      'irsId',
      'irs_submission_id',
      'IRS_SUBMISSION_ID',
      'submission_id',
      'SUBMISSION_ID',
      'IRSSUBMISSION_ID',
      'IRS_SUBMISSIONID',
    ];

    for (const src of sources) {
      if (!src || typeof src !== 'object') {
        continue;
      }
      for (const key of irsKeys) {
        const v = (src as any)[key];
        if (v != null) {
          const s = String(v).trim();
          if (s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
            return s;
          }
        }
      }
    }
    return '';
  }

  /** Parse JSON from CE attributes; tolerate plain objects. */
  private parseBoundContext(val: any): any {
    if (val == null || val === '') {
      return {};
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
      return { ...val };
    }
    const s = String(val).trim();
    if (!s || s === '[object Object]') {
      return {};
    }
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const first = parsed[0];
        return typeof first === 'object' && first !== null ? { ...first } : {};
      }
      return typeof parsed === 'object' && parsed !== null ? { ...parsed } : {};
    } catch {
      return {};
    }
  }

  onCancel(): void {
    this.showModal = false;
    this.requestGridUpdate.emit('cancel');
  }

  onConfirm(): void {
    const irsId = this.resolveIrsId();

    if (!irsId) {
      console.warn(
        '[XmlVsElementsComponent] irs_id not found in any source.',
        {
          filterParams: this.filterParams,
          hostFilterParams: this.hostFilterParams,
          hostRowData: this.hostRowData,
          liveActiveScreenFilters: normalizeFilterParams(
            this.gtwWorkspace.workspaceFactory?.activeScreen || {}
          ),
          globalParams: (this.gtwGlobal as any)?.global?.globalParams || {},
        }
      );
      this.gtwSnackbarService.openSnackBar(
        'Please select an irs submission to extract elements from.',
        'gtw-snackbar--yellow'
      );
      this.onCancel();
      return;
    }

    this.filterParams = { ...(this.filterParams || {}), irs_id: irsId };

    const efileApiUrl =
      this.GENERAL_CONFIG?.gtw_efile_api_url ??
      this.GENERAL_CONFIG?.efile ??
      '';

    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    this.efileAdminService.xmlVsElementsCompare(efileApiUrl, irsId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.detectChanges();
        const data = res?.data ?? res;
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('XML Elements Extraction was Completed!', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred.', 'gtw-snackbar--red');
      }
    });
  }
}
